import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  LogOut, 
  Calendar,
  User,
  MapPin,
  Clock,
  Car,
  Eye,
  Plus,
  Loader2,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [customerName, setCustomerName] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('customerToken');
    const name = sessionStorage.getItem('customerName');
    
    if (!token) {
      navigate('/portal');
      return;
    }
    
    setCustomerName(name || 'Customer');
    fetchBookings();
  }, [navigate]);

  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const token = sessionStorage.getItem('customerToken');
      const response = await axios.get(`${API}/customer/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (error.response?.status === 401) {
        sessionStorage.removeItem('customerToken');
        navigate('/portal');
      }
      toast.error('Failed to load bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('customerToken');
    sessionStorage.removeItem('customerEmail');
    sessionStorage.removeItem('customerName');
    toast.success('Logged out successfully');
    navigate('/portal');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4 text-blue-400" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <AlertCircle className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'confirmed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const tripTypeLabels = {
    airport_transfer: 'Airport Transfer',
    hourly: 'Hourly Service',
    city_to_city: 'City to City',
    event: 'Event'
  };

  const vehicleLabels = {
    sedan: 'Luxury Sedan',
    suv: 'Executive SUV',
    sprinter: 'Sprinter Van'
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="customer-dashboard" className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <Link to="/" className="block">
              <div className="flex flex-col">
                <span className="font-serif text-xl text-white tracking-wide">
                  Capital
                </span>
                <span className="text-[10px] text-[#D4AF37] tracking-[0.2em] uppercase -mt-1">
                  Chauffeur Services
                </span>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <span className="text-white text-sm block">{customerName}</span>
              <span className="text-[#A3A3A3] text-xs">Customer Portal</span>
            </div>
            <Button
              data-testid="customer-logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 rounded-none"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">
            Welcome, {customerName}
          </h1>
          <p className="text-[#A3A3A3]">
            View and manage your reservation requests
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="text-3xl font-serif text-[#D4AF37] mb-2">
              {bookings.length}
            </div>
            <div className="text-[#A3A3A3] text-sm">Total Bookings</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="text-3xl font-serif text-yellow-400 mb-2">
              {bookings.filter(b => b.status === 'pending').length}
            </div>
            <div className="text-[#A3A3A3] text-sm">Pending Confirmation</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="text-3xl font-serif text-green-400 mb-2">
              {bookings.filter(b => b.status === 'completed').length}
            </div>
            <div className="text-[#A3A3A3] text-sm">Completed Trips</div>
          </div>
        </div>

        {/* Book New Ride CTA */}
        <div className="mb-8">
          <Link to="/book">
            <Button
              data-testid="new-booking-btn"
              className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-6 py-5"
            >
              <Plus className="w-4 h-4 mr-2" />
              Book New Ride
            </Button>
          </Link>
        </div>

        {/* Bookings List */}
        <div>
          <h2 className="text-xl font-serif text-white mb-6">Your Reservations</h2>
          
          {bookings.length === 0 ? (
            <div className="bg-[#0A0A0A] border border-white/10 p-12 text-center">
              <Calendar className="w-12 h-12 text-[#A3A3A3] mx-auto mb-4" />
              <h3 className="text-white text-lg mb-2">No Bookings Yet</h3>
              <p className="text-[#A3A3A3] mb-6">
                You haven&apos;t made any reservation requests yet.
              </p>
              <Link to="/book">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none">
                  Book Your First Ride
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div 
                  key={booking.id}
                  className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Left: Trip Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-3 py-1 text-xs font-medium uppercase border ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                        <span className="text-white font-medium">
                          {tripTypeLabels[booking.trip_type]}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-[#A3A3A3]">
                          <Calendar className="w-4 h-4 text-[#D4AF37]" />
                          <span>{booking.pickup_date} at {booking.pickup_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[#A3A3A3]">
                          <Car className="w-4 h-4 text-[#D4AF37]" />
                          <span>{vehicleLabels[booking.vehicle_type]}</span>
                        </div>
                        <div className="flex items-start gap-2 text-[#A3A3A3] sm:col-span-2">
                          <MapPin className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">{booking.pickup_location} → {booking.dropoff_location}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Right: Actions */}
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => setSelectedBooking(booking)}
                        variant="outline"
                        className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-none"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className="mt-12 p-6 bg-[#0A0A0A] border border-[#D4AF37]/20">
          <h3 className="text-lg font-serif text-white mb-4">Need Assistance?</h3>
          <div className="flex flex-wrap gap-6">
            <a href="tel:+12026840534" className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
              <Phone className="w-4 h-4 text-[#D4AF37]" />
              +1 (202) 684-0534
            </a>
            <a href="mailto:contact@capitalchauffeurservices.com" className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
              <Mail className="w-4 h-4 text-[#D4AF37]" />
              contact@capitalchauffeurservices.com
            </a>
          </div>
        </div>
      </main>

      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Reservation Details</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                {getStatusIcon(selectedBooking.status)}
                <span className={`px-3 py-1 text-sm font-medium uppercase ${getStatusColor(selectedBooking.status)}`}>
                  {selectedBooking.status}
                </span>
              </div>

              {/* Trip Details */}
              <div className="space-y-4">
                <div className="p-4 bg-white/5 space-y-3">
                  <h4 className="text-[#D4AF37] text-sm font-medium">Trip Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Service:</span>
                      <span className="text-white">{tripTypeLabels[selectedBooking.trip_type]}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Date:</span>
                      <span className="text-white">{selectedBooking.pickup_date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Time:</span>
                      <span className="text-white">{selectedBooking.pickup_time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Passengers:</span>
                      <span className="text-white">{selectedBooking.passengers}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white/5 space-y-3">
                  <h4 className="text-[#D4AF37] text-sm font-medium">Locations</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-[#A3A3A3] block mb-1">Pickup:</span>
                      <span className="text-white">{selectedBooking.pickup_location}</span>
                    </div>
                    <div>
                      <span className="text-[#A3A3A3] block mb-1">Drop-off:</span>
                      <span className="text-white">{selectedBooking.dropoff_location}</span>
                    </div>
                  </div>
                </div>

                {selectedBooking.return_trip && (
                  <div className="p-4 bg-white/5 space-y-2">
                    <h4 className="text-[#D4AF37] text-sm font-medium">Return Trip</h4>
                    <div className="text-sm">
                      <span className="text-white">
                        {selectedBooking.return_date} at {selectedBooking.return_time}
                      </span>
                    </div>
                  </div>
                )}

                <div className="p-4 bg-white/5 space-y-3">
                  <h4 className="text-[#D4AF37] text-sm font-medium">Vehicle</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Type:</span>
                      <span className="text-white">{vehicleLabels[selectedBooking.vehicle_type]}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A3A3A3]">Luggage:</span>
                      <span className="text-white capitalize">{selectedBooking.luggage_amount}</span>
                    </div>
                  </div>
                </div>

                {selectedBooking.special_requests && (
                  <div className="p-4 bg-white/5 space-y-2">
                    <h4 className="text-[#D4AF37] text-sm font-medium">Special Requests</h4>
                    <p className="text-white text-sm">{selectedBooking.special_requests}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-white/10 text-center">
                <p className="text-[#A3A3A3] text-xs mb-2">
                  Booking ID: {selectedBooking.id.slice(0, 8)}...
                </p>
                <p className="text-[#666] text-xs">
                  Questions? Call us at +1 (202) 684-0534
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
